import React, { useState, useEffect, useRef } from "react";
import MosaicPlot from "./MosaicPlot";
import Tools from "../utils/Tools";
import Select from "react-select";
import ExcelJS from 'exceljs';
import { FiDownload, FiFileText, FiImage } from 'react-icons/fi';

// INTENT AND CATEGORY REFERENCE (for future updates)
const FIXED_INTENTS_REFERENCE = {
    "billing": {
        "high_bill": "Customer disputes bill accuracy or amount. Or customer believes the bill is inaccurate. Agent explains charges or escalates for investigation.",
        "missing_bill": "Customer hasn't received expected bill and seeks explanation for delay.",
        "estimated_bill": "Customer questions revised billing for past period or estimated charges.",
        "unexplained_charge": "Customer seeks clarification about specific charge on bill without necessarily disputing it.",
        "basic_bill_info": "General billing inquiries including payment methods, balance checks, and bill copy requests.",
        "other_bill_inquiry": "General billing questions, refund status checks, or unauthorized charge claims."
    },
    "payments": {
        "misapplied_payment": "Customer needs payment transferred between accounts or payment reversal.",
        "payment_support": "Technical assistance with payment systems or help making payments.",
        "payment_confirm": "Verification of payment receipt and processing.",
        "auto_pay": "Setting up, managing, or troubleshooting automatic payment arrangements.",
        "deposit_refund": "Assistance with deposit refund requests or status checks.",
        "other_payment": "General payment method inquiries and processing questions."
    },
    "delinquency": {
        "payment_extension": "Customer needs payment deadline extension due to inability to pay full amount.",
        "payment_installment": "Customer requests structured payment plan setup.",
        "reconnection": "Disconnected customer seeking service restoration after payment.",
        "arrangement_inquiry": "Questions about existing payment plans or requesting changes.",
        "other_delinquency": "Any other delinquency-related issues not covered above."
    },
    "meter": {
        "meter_work": "Scheduling meter installation or maintenance, excluding solar/net metering.",
        "solar_metering": "Issues regarding solar installation, credits, and net metering.",
        "meter_accuracy": "Customer reports meter reading issues requiring investigation.",
        "other_meter": "General meter inquiries without specific action needed."
    },
    "work": {
        "construction": "Non-urgent construction work requests and scheduling.",
        "damaged_equipment": "Reports of damaged utility equipment requiring field visit.",
        "vegetation": "Requests for tree trimming and debris cleanup near power lines.",
        "property_damage": "Customer reports property damage after utility work.",
        "other_work": "General non-urgent work inquiries and requests."
    },
    "account": {
        "access": "Help with website/app login or account creation.",
        "updates": "Changes to account details, authorized users, or transferring payment plans/account details between accounts.",
        "info_request": "Non-billing account information verification.",
        "other_account": "General account-related inquiries and website navigation help."
    },
    "service": {
        "start": "New service activation and setup.",
        "stop": "Service termination and account closure.",
        "transfer": "Moving service between locations.",
        "modify_order": "Changes to existing service orders or ownership transfers.",
        "other_service": "General service order inquiries and inspection discussions."
    },
    "programs": {
        "budget_billing": "Setup and management of budget billing plans.",
        "state_assist": "Help with income-based payment plans and assistance programs, including PIPP, fuel assistance, cooling assistance, crisis assistance, and Energy Share.",
        "medical_cert": "Medical-related discount programs and equipment.",
        "energy_efficiency": "Information about energy-saving programs and initiatives.",
        "other_programs": "General program inquiries without specific actions."
    },
    "outage": {
        "status": "Updates on current power outages and restoration timing.",
        "non_emergency": "Reporting power outages in area or home, excluding disconnections due to late payments.",
        "street_light": "Problems with street lighting.",
        "emergency": "Reports of immediate safety hazards like downed lines.",
        "other_outage": "General outage protocol questions."
    },
    "misc": {
        "fraud": "Reports of suspicious activity, potential fraud, or identity theft.",
        "rate_plans": "Questions about rate schedules, plan changes, or requests for increased voltage.",
        "other": "Issues not fitting other categories."
    }
};

// Function to filter data based on exclusion configuration
const filterDataByExclusions = (data) => {
    if (!data || !Array.isArray(data)) {
        console.log("FilterDataByExclusions - No data to filter or data is not an array");
        return data;
    }

    console.log("FilterDataByExclusions - Starting with", data.length, "items");

    // Create intent to category map
    let intent_to_category_map = {};
    for (const categoryName in FIXED_INTENTS_REFERENCE) {
        for (const intentName in FIXED_INTENTS_REFERENCE[categoryName]) {
            intent_to_category_map[intentName] = categoryName;
        }
    }

    const filteredData = data.filter(item => {
        const categoryName = item.category_name;
        const intentName = item.intent_name;

        // Check if the current category and intent combination is valid
        if (FIXED_INTENTS_REFERENCE[categoryName] && FIXED_INTENTS_REFERENCE[categoryName][intentName]) {
            return true;
        }

        // If intent exists but in wrong category, fix the category
        if (intent_to_category_map[intentName]) {
            item.category_name = intent_to_category_map[intentName];
            console.log(`Fixed category for intent "${intentName}" from "${categoryName}" to "${intent_to_category_map[intentName]}"`);
            return true;
        }

        // If intent doesn't exist in our reference, remove it
        console.log(`Removing item with unknown intent: "${intentName}" in category: "${categoryName}"`);
        return false;
    });

    console.log("FilterDataByExclusions - After filtering:", filteredData.length, "items remain");

    // Log summary of what was kept
    const categorySummary = {};
    filteredData.forEach(item => {
        if (!categorySummary[item.category_name]) {
            categorySummary[item.category_name] = new Set();
        }
        categorySummary[item.category_name].add(item.intent_name);
    });

    console.log("FilterDataByExclusions - Remaining categories and intents:",
        Object.entries(categorySummary).map(([cat, intents]) => ({
            category: cat,
            intents: Array.from(intents)
        }))
    );

    return filteredData;
};

// Use the same formatCellName function as MosaicPlot to ensure consistency
const formatCellName = (str) => {
    if (!str) return str;
    // Replace underscores with spaces
    str = str.replace(/_/g, " ");
    // Capitalize the first letter of the string
    str = str.charAt(0).toUpperCase() + str.slice(1);
    // Capitalize letters after spaces
    str = str.replace(/\s[a-z]/g, (letter) => letter.toUpperCase());
    return str;
};

// Copy the data processing functions from MosaicPlot to ensure consistency
const combineDuplicates = (data) => {
    const combinedMap = new Map();

    for (let item of data) {
        const key = `${item.columnName}-${item.rowName}`;
        if (combinedMap.has(key)) {
            const entry = combinedMap.get(key);
            entry.primaryCount += item.primaryCount;
            entry.secondaryCount += item.secondaryCount;
        } else {
            combinedMap.set(key, { ...item });
        }
    }

    return Array.from(combinedMap.values());
};

const groupByCategory = (data) => {
    return Object.entries(
        data.reduce((acc, item) => {
            if (!acc[item.columnName]) {
                acc[item.columnName] = [];
            }
            acc[item.columnName].push(item);
            return acc;
        }, {})
    ).map(([columnName, items]) => ({ columnName, items }));
};

const convertKeyNames = (data, rowName, columnName, primaryCount, secondaryCount) => {
    const convertedArray = data.map((obj) => ({
        rowName: obj[rowName],
        columnName: obj[columnName],
        primaryCount: obj[primaryCount],
        secondaryCount: obj[secondaryCount],
    }));
    return convertedArray;
};

const processData = (
    data,
    otherRowThreshold,
    otherRowHeightThresHold,
    rowName,
    columnName,
    primaryCount,
    secondaryCount
) => {
    const newData = convertKeyNames(
        data,
        rowName,
        columnName,
        primaryCount,
        secondaryCount
    );
    const processedData = groupByCategory(combineDuplicates(newData))
        .sort((a, b) => {
            // Sort in descending order by columnNames.
            const totalCountA = a.items.reduce(
                (sum, item) => sum + item.primaryCount,
                0
            );
            const totalCountB = b.items.reduce(
                (sum, item) => sum + item.primaryCount,
                0
            );
            return totalCountB - totalCountA;
        })
        .map(({ columnName, items }) => {
            // Get total to calculate threshold.
            const totalCount = items.reduce(
                (sum, item) => sum + item.primaryCount,
                0
            );

            const threshold = Math.ceil(totalCount * (1 - otherRowThreshold));

            // Sort the items in descending order.
            const sortedItems = items.sort((a, b) => b.primaryCount - a.primaryCount);

            const processedItems = [];

            for (let i = 0, sum = 0; i < sortedItems.length; i++) {
                const item = sortedItems[i];

                // If the row is too small of a percentage, just turn everything smaller than it into the other row.
                const rowTooSmall =
                    item.primaryCount / totalCount < otherRowHeightThresHold;

                if ((sum > threshold && i !== sortedItems.length - 1) || rowTooSmall) {
                    // Group remaining items into "other"
                    const remainingItems = sortedItems.slice(i).reverse();

                    const otherCount = remainingItems.reduce(
                        (sum, item) => sum + item.primaryCount,
                        0
                    );

                    const otherSecondaryCount = remainingItems.reduce(
                        (sum, item) => sum + item.secondaryCount,
                        0
                    );

                    processedItems.push({
                        columnName: columnName,
                        rowName: "Other",
                        primaryCount: otherCount,
                        secondaryCount: otherSecondaryCount,
                        otherItems: remainingItems,
                    });

                    break;
                } else {
                    processedItems.push(item);
                    sum += item.primaryCount;
                }
            }

            processedItems.reverse();

            return {
                columnName,
                items: processedItems,
            };
        })
        .flatMap(({ items }) => items);
    return processedData;
};

// Consolidated color configuration
const CATEGORY_COLORS = {
    "Service": "#e6404e",
    "Billing": "#29c068",
    "Delinquency": "#7061d1",
    "Payments": "#e69622",     // Kept as warm orange
    "Account": "#549ccb",
    "Meter": "#b03e5f",
    "Outage": "#e6d435",       // Adjusted to a more distinct yellow
    "Work": "#45bbcb",
    "Programs": "#a75eb4",
    "Misc": "#6c828c",
};

const FixedTaxonomyPage = () => {
    const today = getCurrentDate();

    const [selectedDateStart, setSelectedDateStart] = useState(today);
    const [selectedDateEnd, setSelectedDateEnd] = useState(today);

    // For naming downloaded file
    const [chartDateStart, setChartDateStart] = useState("");
    const [chartDateEnd, setChartDateEnd] = useState("");

    const [chartData, setChartData] = useState(null);

    // Keep track of window size so the chart can use it
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            setWindowHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const [allQueues, setAllQueues] = useState([]);
    const [selectedQueues, setSelectedQueues] = useState([]);
    const [chartQueueName, setChartQueueName] = useState("");

    useEffect(() => {
        const init = async () => {
            const results = await Tools.sendRequest("get-fixed-taxonomy-data", {
                init: true,
            });

            setAllQueues(
                Object.keys(results.queueCounts)
                    .map((a) => ({
                        value: a,
                        label: a,
                        count: results.queueCounts[a],
                    }))
                    .sort((a, b) => b.count - a.count) // Sort by count descending
            );
        };
        init();
    }, []);

    // Reference the SVG so we can download it
    const svgRef = useRef();

    // Function to get friendly name for intents (not categories)
    const getFriendlyName = (name) => {
        if (!name) return name;
        // Convert snake_case or camelCase to Title Case
        return name
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const downloadXLSX = async () => {
        if (!chartData || !chartData.chartData) {
            return;
        }

        console.log("DownloadXLSX - Using filtered chart data:", chartData.chartData.length, "items");

        // Use the same parameters as MosaicPlot to ensure consistency
        const otherRowThreshold = 0.05;
        const otherRowHeightThresHold = 0.023;
        const rowName = "intent_name";
        const columnName = "category_name";
        const primaryCount = "total_call_duration_minutes";
        const secondaryCount = "call_count";

        // Apply the same data processing as MosaicPlot (chartData.chartData is already filtered)
        const processedData = processData(
            chartData.chartData,
            otherRowThreshold,
            otherRowHeightThresHold,
            rowName,
            columnName,
            primaryCount,
            secondaryCount
        );

        console.log("DownloadXLSX - Processed data for Excel:", processedData.length, "items");

        // Create a new workbook
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Fixed Intents Report');

        // Group data by category using the processed data
        const groupedData = {};
        let totalMinutes = 0;
        let totalContacts = 0;

        processedData.forEach((row) => {
            // Use formatCellName to match MosaicPlot's category naming
            const translatedCategoryName = formatCellName(row.columnName);
            const intentName = row.rowName === "Other" ? "Other" : getFriendlyName(row.rowName);

            const minutes = Math.round(row.primaryCount || 0);
            const contacts = row.secondaryCount || 0;

            totalMinutes += minutes;
            totalContacts += contacts;

            if (!groupedData[translatedCategoryName]) {
                groupedData[translatedCategoryName] = {
                    totalMinutes: 0,
                    totalContacts: 0,
                    intents: [],
                    originalName: row.columnName
                };
            }

            groupedData[translatedCategoryName].totalMinutes += minutes;
            groupedData[translatedCategoryName].totalContacts += contacts;

            if (row.otherItems) {
                // Handle "Other" items specially
                row.otherItems.forEach(otherItem => {
                    groupedData[translatedCategoryName].intents.push({
                        intent: getFriendlyName(otherItem.rowName),
                        minutes: Math.round(otherItem.primaryCount),
                        contacts: otherItem.secondaryCount
                    });
                });
            } else {
                groupedData[translatedCategoryName].intents.push({
                    intent: intentName,
                    minutes: minutes,
                    contacts: contacts
                });
            }
        });

        // Set column widths
        worksheet.columns = [
            { header: '', key: 'name', width: 35 },
            { header: '', key: 'contacts', width: 15 },
            { header: '', key: 'minutes', width: 15 },
            { header: '', key: 'percentage', width: 12 }
        ];

        // Add header row
        const headerRow = worksheet.addRow([
            `Fixed Intents Report - ${chartDateStart} to ${chartDateEnd}${chartQueueName ? ' - ' + chartQueueName : ''}`
        ]);
        headerRow.font = { bold: true, size: 14 };
        worksheet.mergeCells('A1:D1');

        // Add empty row
        worksheet.addRow([]);

        // Add column headers
        const columnHeaderRow = worksheet.addRow(['Category/Intent', 'Total Contacts', 'Total Minutes', 'Percentage']);
        columnHeaderRow.font = { bold: true };

        // Sort categories by total minutes (descending) to match MosaicPlot order
        const sortedCategories = Object.keys(groupedData).sort((a, b) =>
            groupedData[b].totalMinutes - groupedData[a].totalMinutes
        );

        console.log("DownloadXLSX - Sorted categories:", sortedCategories);

        // Process each category
        sortedCategories.forEach(translatedCategoryName => {
            const categoryData = groupedData[translatedCategoryName];
            const percentage = totalMinutes > 0 ? ((categoryData.totalMinutes / totalMinutes) * 100).toFixed(1) : 0;

            // Skip categories with 0%
            if (parseFloat(percentage) === 0) {
                return;
            }

            // Add category row
            const categoryRow = worksheet.addRow([
                translatedCategoryName,
                categoryData.totalContacts,
                Math.round(categoryData.totalMinutes),
                `${percentage}%`
            ]);

            // Get category color using the translated name
            let categoryColor = CATEGORY_COLORS[translatedCategoryName];
            let backgroundColor = 'FFBDBDBD'; // Default gray for unknown categories

            if (categoryColor) {
                // Convert hex to ARGB (remove # and add FF prefix for alpha)
                backgroundColor = 'FF' + categoryColor.replace('#', '');
            }

            categoryRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: backgroundColor }
                };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            });

            // Sort intents by minutes (descending)
            const sortedIntents = categoryData.intents.sort((a, b) => b.minutes - a.minutes);

            // Add intent rows for this category
            sortedIntents.forEach(intent => {
                if (intent.minutes > 0) { // Only include intents with actual minutes
                    const intentPercentage = categoryData.totalMinutes > 0 ? ((intent.minutes / categoryData.totalMinutes) * 100).toFixed(1) : 0;
                    worksheet.addRow([
                        `  ${intent.intent}`, // Indent intent names
                        intent.contacts,
                        Math.round(intent.minutes),
                        `${intentPercentage}%`
                    ]);
                }
            });

            // Add empty row between categories
            worksheet.addRow([]);
        });

        // Add totals row
        const totalRow = worksheet.addRow(['TOTAL', totalContacts, Math.round(totalMinutes), '100.0%']);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE6E6E6' } // Light gray background
            };
        });

        // Generate filename and save
        const filename = `fixed_intents_data_${chartDateStart}_${chartDateEnd}${chartQueueName ? '_' + chartQueueName.replace(/[^a-zA-Z0-9]/g, '_') : ''}.xlsx`;
        console.log("DownloadXLSX - Saving as:", filename);

        // Create buffer and download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    const downloadPNG = () => {
        if (svgRef.current) {
            console.log("DownloadPNG - Starting PNG download");
            // Get the SVG data
            const svgData = new XMLSerializer().serializeToString(svgRef.current);

            // Create a canvas element
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            // Create an image to draw to canvas
            const img = new Image();

            img.onload = () => {
                // Set canvas dimensions
                canvas.width = img.width;
                canvas.height = img.height + 60; // Add extra height for the title

                // Draw white background (transparent otherwise)
                ctx.fillStyle = "white";
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Add title
                ctx.fillStyle = "black";
                ctx.font = "bold 25px Inter";
                ctx.textAlign = "center";
                ctx.fillText(
                    `Fixed Intents ${chartDateStart} to ${chartDateEnd}${chartQueueName ? " - " + chartQueueName : ""
                    }`,
                    canvas.width / 2,
                    40
                );

                // Draw image to canvas
                ctx.drawImage(img, 0, 60); // Offset the image to make room for the title

                // Create PNG download
                const pngFile = canvas.toDataURL("image/png");
                const downloadLink = document.createElement("a");
                downloadLink.download = `fixed_intents_chart_${chartDateStart}_${chartDateEnd}${chartQueueName ? "_" + chartQueueName : ""
                    }.png`;
                downloadLink.href = pngFile;

                // Trigger download
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);

                console.log("DownloadPNG - PNG download completed");
            };

            // Load SVG data into image
            img.src =
                "data:image/svg+xml;base64," +
                btoa(unescape(encodeURIComponent(svgData)));
        }
    };

    const handleStartDateChange = (event) => {
        setSelectedDateStart(event.target.value);
    };

    const handleEndDateChange = (event) => {
        setSelectedDateEnd(event.target.value);
    };
    const [loading, setLoading] = useState(false);

    const customStyles = {
        control: (provided) => ({
            ...provided,
            width: "350px", // Increased from 275px
            minHeight: "42px", // Use minHeight instead of fixed height
            height: "auto", // Allow it to grow as selections are added
        }),
        menu: (provided) => ({
            ...provided,
            width: "auto",
            minWidth: "350px", // Match control width
            maxWidth: "700px", // Doubled for menu options
            left: "50%",
            transform: "translateX(-50%)",
        }),
        menuList: (provided) => ({
            ...provided,
            overflowX: "auto",
        }),
        multiValue: (provided) => ({
            ...provided,
            // Make selected items more compact
            margin: "2px",
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            // Slightly smaller text for selected items
            fontSize: "85%",
            padding: "3px 6px",
        }),
        valueContainer: (provided) => ({
            ...provided,
            // Better handling of multiple selections
            flexWrap: "wrap",
            overflowY: "auto",
            padding: "2px 6px",
        }),
        placeholder: (provided) => ({
            ...provided,
            whiteSpace: "nowrap",
        }),
    };

    const formatOptionLabel = ({ label, count }, { context }) => {
        if (context === "menu") {
            return (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{label}</span>
                    {count !== undefined && (
                        <span style={{ color: "#aaa", paddingLeft: "5px" }}>({count})</span>
                    )}
                </div>
            );
        } else {
            return label;
        }
    };

    return (
        <div className="container mx-auto  flex flex-col items-center">
            <div className="mb-4 p-4 rounded-lg mt-4">
                <div className="flex flex-row justify-center items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700">
                        Start Date:
                    </label>
                    <input
                        type="date"
                        value={selectedDateStart}
                        className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md w-[175px]"
                        onChange={handleStartDateChange}
                        onBlur={handleStartDateChange}
                        max={selectedDateEnd}
                    />
                    <label className="text-sm font-medium text-gray-700">End Date:</label>
                    <input
                        type="date"
                        value={selectedDateEnd}
                        className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md w-[175px]"
                        onChange={handleEndDateChange}
                        onBlur={handleEndDateChange}
                        min={selectedDateStart}
                        max={today}
                    />
                    <Select
                        name="queue"
                        isClearable
                        placeholder="Select Queue"
                        options={allQueues}
                        isMulti
                        onChange={(selected) => {
                            const selectedQueues = selected.map((obj) => obj.value);
                            setSelectedQueues(selectedQueues);
                        }}
                        styles={customStyles}
                        formatOptionLabel={formatOptionLabel}
                    />
                    <button
                        onClick={async () => {
                            // Validate dates
                            if (selectedDateStart > selectedDateEnd) {
                                alert("Start date cannot be later than the end date.");
                                return;
                            }
                            if (selectedDateEnd > today) {
                                alert("End date cannot be later than today.");
                                return;
                            }

                            setLoading(true);

                            const params = {
                                startDate: selectedDateStart,
                                endDate: selectedDateEnd,
                            };

                            if (selectedQueues) {
                                params.queue = selectedQueues;
                            }

                            console.log("Generate Chart - Requesting data with params:", params);
                            const results = await Tools.sendRequest(
                                "get-fixed-taxonomy-data",
                                params
                            );
                            console.log("Generate Chart - Raw data received:", results);

                            if (results.chartData && results.chartData.length) {
                                console.log("Generate Chart - Raw chart data length:", results.chartData.length);

                                // APPLY EXCLUSION FILTERING HERE
                                const filteredChartData = filterDataByExclusions(results.chartData);
                                console.log("Generate Chart - Filtered chart data length:", filteredChartData.length);

                                // Check if we still have data after filtering
                                if (filteredChartData.length === 0) {
                                    alert("No data remains after applying exclusion filters.");
                                    setLoading(false);
                                    return;
                                }

                                // Update the results with filtered data
                                results.chartData = filteredChartData;

                                // Use consolidated color configuration
                                results.columnColorOverrides = CATEGORY_COLORS;
                                console.log("Generate Chart - Color overrides applied:", CATEGORY_COLORS);

                                setChartData(results);
                                setChartDateStart(selectedDateStart);
                                setChartDateEnd(selectedDateEnd);
                                setChartQueueName(selectedQueues.join(", "));

                                console.log("Generate Chart - Chart data set successfully");
                            } else {
                                console.log("Generate Chart - No data found in results");
                                alert("No data was found for this set of options.");
                            }
                            setLoading(false);
                        }}
                        className="px-4 py-1 usan-primary text-white rounded hover:bg-blue-600 text-sm"
                    >
                        Generate Chart
                    </button>
                    <button
                        onClick={downloadPNG}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium
             hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2
             disabled:bg-gray-400 disabled:text-gray-200 disabled:cursor-not-allowed
             shadow-sm hover:shadow-md"
                        disabled={!chartData}
                    >
                        <FiImage className="w-4 h-4" />
                        <span>Download Chart</span>
                    </button>
                    <button
                        onClick={downloadXLSX}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium
             hover:bg-green-700 transition-colors duration-200 flex items-center space-x-2
             disabled:bg-gray-400 disabled:text-gray-200 disabled:cursor-not-allowed
             shadow-sm hover:shadow-md"
                        disabled={!chartData}
                    >
                        <FiFileText className="w-4 h-4" />
                        <span>Download Excel</span>
                    </button>
                </div>
            </div>
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="loader"></div>
                </div>
            ) : chartData ? (
                <MosaicPlot
                    data={chartData}
                    width={windowWidth - 200}
                    height={windowHeight - 100}
                    rowName="intent_name"
                    columnName="category_name"
                    primaryCount="total_call_duration_minutes"
                    secondaryCount="call_count"
                    svgRef={svgRef}
                />
            ) : (
                <></>
            )}
        </div>
    );
};

export default FixedTaxonomyPage;

const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};